import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrashIcon,
  MinusIcon,
  PlusIcon,
  ShoppingBagIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import useCartStore from '../store/cartStore';

const CartPage = () => {
  const { t, i18n } = useTranslation();
  const { items, removeItem, updateQuantity, getTotal, getItemsByStore } = useCartStore();

  const getName = (product) => {
    const lang = i18n.language;
    if (lang === 'ar' && product.name_ar) return product.name_ar;
    if (lang === 'en' && product.name_en) return product.name_en;
    if (lang === 'ku' && product.name_ku) return product.name_ku;
    return product.name;
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center py-12">
          <ShoppingBagIcon className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4 text-gray-900">{t('cart.empty') || 'السلة فارغة'}</h1>
          <p className="text-gray-600 mb-8">ابدأ بإضافة المنتجات إلى سلة التسوق</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            تصفح المتاجر
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </div>
    );
  }

  // Group items by store
  const itemsByStore = getItemsByStore();
  const storeCount = Object.keys(itemsByStore).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">{t('common.cart') || 'السلة'}</h1>
        
        {storeCount > 1 && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-blue-800 font-semibold">
              📦 لديك منتجات من {storeCount} متاجر مختلفة. سيتم إنشاء طلب منفصل لكل متجر.
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items - Grouped by Store */}
          <div className="lg:col-span-2 space-y-6">
            {Object.entries(itemsByStore).map(([storeSlug, storeData]) => (
              <div key={storeSlug} className="bg-white rounded-2xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <ShoppingBagIcon className="w-6 h-6 text-blue-600" />
                    {storeData.storeName}
                  </h2>
                  <span className="text-sm text-gray-500">{storeData.items.length} منتج</span>
                </div>
                
                <div className="space-y-4">
                  {storeData.items.map((item) => (
                    <div
                      key={item.product.id}
                      className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pb-4 border-b border-gray-100 last:border-b-0"
                    >
                      {/* Image */}
                      <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        {item.product.images && item.product.images[0] ? (
                          <img
                            src={item.product.images[0]}
                            alt={getName(item.product)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ShoppingBagIcon className="w-10 h-10" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-grow min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                          {getName(item.product)}
                        </h3>
                        <p className="text-blue-600 font-bold">
                          {item.product.price?.toLocaleString() || '0'} د.ع
                        </p>
                      </div>
                      
                      {/* Quantity */}
                      <div className="flex items-center gap-2 border-2 border-gray-200 rounded-lg">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="p-2 hover:bg-gray-100 transition-colors"
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <span className="w-12 text-center font-bold text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="p-2 hover:bg-gray-100 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Subtotal */}
                      <div className="text-left sm:text-right w-full sm:w-auto">
                        <p className="font-bold text-lg text-gray-900">
                          {(item.product.price * item.quantity).toLocaleString()} د.ع
                        </p>
                      </div>
                      
                      {/* Remove */}
                      <button
                        onClick={() => removeItem(item.product.id)}
                        className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-2xl shadow-md sticky top-24">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">ملخص الطلب</h2>
              
              {storeCount > 1 && (
                <div className="mb-4 space-y-2">
                  <p className="text-sm font-semibold text-gray-700 mb-2">حسب المتجر:</p>
                  {Object.entries(itemsByStore).map(([storeSlug, storeData]) => (
                    <div key={storeSlug} className="flex justify-between text-sm">
                      <span className="text-gray-600">{storeData.storeName}</span>
                      <span className="font-semibold text-gray-900">
                        {storeData.items.reduce((total, item) => 
                          total + (item.product.price * item.quantity), 0
                        ).toLocaleString()} د.ع
                      </span>
                    </div>
                  ))}
                  <div className="border-t pt-2"></div>
                </div>
              )}
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>المجموع الفرعي</span>
                  <span className="font-semibold text-gray-900">{getTotal().toLocaleString()} د.ع</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>الشحن</span>
                  <span className="font-semibold text-gray-900">0 د.ع</span>
                </div>
                <div className="border-t-2 border-gray-200 pt-4 flex justify-between text-2xl font-bold">
                  <span className="text-gray-900">{t('common.total') || 'الإجمالي'}</span>
                  <span className="text-blue-600">{getTotal().toLocaleString()} د.ع</span>
                </div>
              </div>
              
              {storeCount > 1 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-yellow-800">
                    ℹ️ سيتم إنشاء {storeCount} طلبات منفصلة (طلب لكل متجر)
                  </p>
                </div>
              )}
              
              <Link
                to="/checkout"
                className="block w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center px-6 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all transform hover:scale-105 mb-4"
              >
                {t('cart.checkout') || 'إتمام الطلب'}
              </Link>
              
              <Link
                to="/"
                className="block w-full text-center text-blue-600 hover:text-blue-700 font-semibold transition-colors"
              >
                متابعة التسوق
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
